import React, { useCallback, useEffect, useLayoutEffect } from 'react'
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronButtonContainer, Content, InfoContainer, LeftContainer } from './styles'
import {
  collapsedLeftContainerWidth,
  DEFAULT_PATH,
  expandedLeftContainerWidth,
  SEARCH_PATIENT_PATH,
  TIMELINE_GENERAL_PATH
} from '~/constants'
import { PatientProfile } from '~/components/patient/patientProfile'
import { MenuSidebar } from '~/components/menuSidebar'
import { OpenCloseProvider, useOpenCloseState, useOpenCloseUpdater, usePatient } from '~/context'
import { hasObjectValue } from '~/utils'
import { ExpandAndCollapseButton, FeatureFlag } from '~/components/shared'
import { LocalStorageManager } from '~/modules'
import { Divider } from '~/styles'
import { NPSSurvey } from '~/components/NPSSurvey'
import { OpenChatLLM } from '~/components/chatLLM/OpenChatLLM'
import { ChatLLM } from '~/components/chatLLM/Chat'

const DashboardLeftContainer = (): JSX.Element => {
  const { isOpen } = useOpenCloseState()
  const { open, close } = useOpenCloseUpdater()

  const leftContainerWidth = isOpen ? expandedLeftContainerWidth : collapsedLeftContainerWidth

  const toggleLeftContainer = useCallback(() => {
    if (isOpen) close()
    if (!isOpen) open()
  }, [isOpen, open, close])

  return (
    <LeftContainer leftWidth={leftContainerWidth} data-testid="leftContainer">
      <InfoContainer>
        <PatientProfile isCollapsed={!isOpen} />
        <Divider />
        <MenuSidebar isCollapsed={!isOpen} />
      </InfoContainer>
      <ChevronButtonContainer leftWidth={leftContainerWidth}>
        <ExpandAndCollapseButton isCollapsed={!isOpen} onClick={toggleLeftContainer} />
      </ChevronButtonContainer>
    </LeftContainer>
  )
}

export const Dashboard = (): JSX.Element => {
  const { medicalRecord } = useParams()
  const [searchParams] = useSearchParams()

  const { patient, getPatient, errorPatient } = usePatient()
  const navigate = useNavigate()

  const backToSearchPatient = useCallback(() => navigate(`/${DEFAULT_PATH}/${SEARCH_PATIENT_PATH}`), [navigate])
  const navigateToSearchedPatient = useCallback(
    (medicalRecord: number | string) => navigate(`/${DEFAULT_PATH}/${TIMELINE_GENERAL_PATH}/${medicalRecord}`),
    [navigate]
  )

  useEffect(() => {
    const fetchPatientByMedicalRecord = () => {
      if (medicalRecord && !hasObjectValue({ ...patient })) {
        getPatient({ pathParams: { medicalRecord: +medicalRecord } })
        LocalStorageManager.DeleteData('episode_number-encrypted')
      }
    }

    const personQueryParam = searchParams.get('episodeNumber')
    const isRawPath = !medicalRecord && !personQueryParam
    const isQueryParamPath = !medicalRecord && personQueryParam

    if (isRawPath) {
      return backToSearchPatient()
    }

    if (isQueryParamPath) {
      LocalStorageManager.WriteEncryptedData('episode_number-encrypted', personQueryParam)
      return navigateToSearchedPatient(personQueryParam)
    }

    fetchPatientByMedicalRecord()
  }, [patient, searchParams, medicalRecord, getPatient, backToSearchPatient, navigateToSearchedPatient])

  useLayoutEffect(() => {
    if (errorPatient) {
      backToSearchPatient()
    }
  }, [errorPatient, backToSearchPatient])

  return (
    <Content>
      <OpenCloseProvider>
        <DashboardLeftContainer />
      </OpenCloseProvider>
      <Outlet />
      <NPSSurvey />

      <FeatureFlag flag="InteractiveAI">
        <OpenCloseProvider initialValue={false}>
          <OpenChatLLM />
          <ChatLLM />
        </OpenCloseProvider>
      </FeatureFlag>
    </Content>
  )
}
